import type { PortalAuthResponse } from "@klyra/shared";
/**
 * Patient-portal session state — completely separate from the staff
 * auth store so a clinic owner can preview a patient link without
 * losing their own session.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface PortalState {
	token: string | null;
	expiresAt: string | null;
	patient: PortalAuthResponse["patient"] | null;
	clinic: PortalAuthResponse["clinic"] | null;

	setSession: (data: PortalAuthResponse) => void;
	clearSession: () => void;
	isAuthenticated: () => boolean;
	isExpired: () => boolean;
}

export const usePortalStore = create<PortalState>()(
	persist(
		(set, get) => ({
			token: null,
			expiresAt: null,
			patient: null,
			clinic: null,

			setSession: (data) =>
				set({
					token: data.token,
					expiresAt: data.expires_at,
					patient: data.patient,
					clinic: data.clinic,
				}),

			clearSession: () =>
				set({ token: null, expiresAt: null, patient: null, clinic: null }),

			isAuthenticated: () => {
				const s = get();
				if (!s.token || !s.expiresAt) return false;
				return new Date(s.expiresAt) > new Date();
			},

			isExpired: () => {
				const s = get();
				if (!s.expiresAt) return false;
				return new Date(s.expiresAt) <= new Date();
			},
		}),
		{
			name: "klyra.portal",
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				token: state.token,
				expiresAt: state.expiresAt,
				patient: state.patient,
				clinic: state.clinic,
			}),
		},
	),
);
